import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import type { IntlShape } from 'react-intl'
dayjs.extend(utc)
dayjs.extend(timezone)
type FormatMessageFunction = IntlShape['formatMessage']

export interface TimezoneOption {
  value: string
  offset: string
  label: string
}

const COMMON_TIMEZONES = [
  // UTC-12 to UTC-1
  'Etc/GMT-12', // UTC-12
  'Pacific/Samoa', // UTC-11
  'Pacific/Honolulu', // UTC-10
  'America/Anchorage', // UTC-9
  'America/Los_Angeles', // UTC-8
  'America/Denver', // UTC-7
  'America/Chicago', // UTC-6
  'America/New_York', // UTC-5
  'America/Halifax', // UTC-4
  'America/Sao_Paulo', // UTC-3
  'Atlantic/South_Georgia', // UTC-2
  'Atlantic/Azores', // UTC-1

  // UTC±0
  'UTC', // UTC±0

  // UTC+1 to UTC+14
  'Europe/Paris', // UTC+1
  'Africa/Cairo', // UTC+2
  'Europe/Moscow', // UTC+3
  'Asia/Dubai', // UTC+4
  'Asia/Karachi', // UTC+5
  'Asia/Kolkata', // UTC+5:30
  'Asia/Dhaka', // UTC+6
  'Asia/Bangkok', // UTC+7
  'Asia/Shanghai', // UTC+8
  'Asia/Tokyo', // UTC+9
  'Australia/Adelaide', // UTC+9:30
  'Australia/Sydney', // UTC+10
  'Pacific/Noumea', // UTC+11
  'Pacific/Auckland', // UTC+12
  'Pacific/Tongatapu', // UTC+13
  'Pacific/Kiritimati', // UTC+14
]

const getTimezoneOffset = (tz: string): string => {
  try {
    if (tz === 'UTC') return 'UTC±0'

    // Use January 15th to avoid daylight saving time issues
    const winterDate = dayjs('2024-01-15T12:00:00').tz(tz)
    const offsetMinutes = winterDate.utcOffset()
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60)
    const offsetMins = Math.abs(offsetMinutes) % 60
    const sign = offsetMinutes >= 0 ? '+' : '-'

    if (offsetMins === 0) {
      return `UTC${sign}${offsetHours}`
    } else {
      return `UTC${sign}${offsetHours}:${offsetMins.toString().padStart(2, '0')}`
    }
  } catch {
    return 'UTC±0'
  }
}

const getCityTranslation = (
  city: string,
  formatMessage: FormatMessageFunction,
): string => {
  switch (city) {
    // UTC-12 to UTC-1
    case 'GMT-12':
      return formatMessage({
        id: 'timezone.city.GMT-12',
        defaultMessage: 'International Date Line West',
      })
    case 'Samoa':
      return formatMessage({
        id: 'timezone.city.Samoa',
        defaultMessage: 'Samoa',
      })
    case 'Honolulu':
      return formatMessage({
        id: 'timezone.city.Honolulu',
        defaultMessage: 'Honolulu',
      })
    case 'Anchorage':
      return formatMessage({
        id: 'timezone.city.Anchorage',
        defaultMessage: 'Anchorage',
      })
    case 'Los Angeles':
      return formatMessage({
        id: 'timezone.city.LosAngeles',
        defaultMessage: 'Los Angeles',
      })
    case 'Denver':
      return formatMessage({
        id: 'timezone.city.Denver',
        defaultMessage: 'Denver',
      })
    case 'Chicago':
      return formatMessage({
        id: 'timezone.city.Chicago',
        defaultMessage: 'Chicago',
      })
    case 'New York':
      return formatMessage({
        id: 'timezone.city.NewYork',
        defaultMessage: 'New York',
      })
    case 'Halifax':
      return formatMessage({
        id: 'timezone.city.Halifax',
        defaultMessage: 'Halifax',
      })
    case 'Sao Paulo':
      return formatMessage({
        id: 'timezone.city.SaoPaulo',
        defaultMessage: 'São Paulo',
      })
    case 'South Georgia':
      return formatMessage({
        id: 'timezone.city.SouthGeorgia',
        defaultMessage: 'South Georgia',
      })
    case 'Azores':
      return formatMessage({
        id: 'timezone.city.Azores',
        defaultMessage: 'Azores',
      })

    // UTC+1 to UTC+14
    case 'Paris':
      return formatMessage({
        id: 'timezone.city.Paris',
        defaultMessage: 'Paris',
      })
    case 'Cairo':
      return formatMessage({
        id: 'timezone.city.Cairo',
        defaultMessage: 'Cairo',
      })
    case 'Moscow':
      return formatMessage({
        id: 'timezone.city.Moscow',
        defaultMessage: 'Moscow',
      })
    case 'Dubai':
      return formatMessage({
        id: 'timezone.city.Dubai',
        defaultMessage: 'Dubai',
      })
    case 'Karachi':
      return formatMessage({
        id: 'timezone.city.Karachi',
        defaultMessage: 'Karachi',
      })
    case 'Kolkata':
      return formatMessage({
        id: 'timezone.city.Kolkata',
        defaultMessage: 'Kolkata',
      })
    case 'Dhaka':
      return formatMessage({
        id: 'timezone.city.Dhaka',
        defaultMessage: 'Dhaka',
      })
    case 'Bangkok':
      return formatMessage({
        id: 'timezone.city.Bangkok',
        defaultMessage: 'Bangkok',
      })
    case 'Shanghai':
      return formatMessage({
        id: 'timezone.city.Shanghai',
        defaultMessage: 'Shanghai',
      })
    case 'Tokyo':
      return formatMessage({
        id: 'timezone.city.Tokyo',
        defaultMessage: 'Tokyo',
      })
    case 'Adelaide':
      return formatMessage({
        id: 'timezone.city.Adelaide',
        defaultMessage: 'Adelaide',
      })
    case 'Sydney':
      return formatMessage({
        id: 'timezone.city.Sydney',
        defaultMessage: 'Sydney',
      })
    case 'Noumea':
      return formatMessage({
        id: 'timezone.city.Noumea',
        defaultMessage: 'Noumea',
      })
    case 'Auckland':
      return formatMessage({
        id: 'timezone.city.Auckland',
        defaultMessage: 'Auckland',
      })
    case 'Tongatapu':
      return formatMessage({
        id: 'timezone.city.Tongatapu',
        defaultMessage: 'Tongatapu',
      })
    case 'Kiritimati':
      return formatMessage({
        id: 'timezone.city.Kiritimati',
        defaultMessage: 'Kiritimati',
      })
    default:
      return city
  }
}

const formatTimezoneLabel = (
  tz: string,
  formatMessage: FormatMessageFunction,
): string => {
  try {
    if (tz === 'UTC') {
      const utcLabel = formatMessage({
        id: 'timezone.utc',
        defaultMessage: 'Universal Time',
      })
      return `UTC±0 - ${utcLabel}`
    }

    const offset = getTimezoneOffset(tz)
    const parts = tz.split('/')
    const city = parts[parts.length - 1].replace(/_/g, ' ')
    const translatedCity = getCityTranslation(city, formatMessage)
    return `${offset} - ${translatedCity}`
  } catch {
    return tz
  }
}

export const getTimezoneSelectOptions = (
  formatMessage: FormatMessageFunction,
) => {
  const timezones: TimezoneOption[] = COMMON_TIMEZONES.map(tz => ({
    value: tz,
    offset: getTimezoneOffset(tz),
    label: formatTimezoneLabel(tz, formatMessage),
  }))

  return timezones
    .sort((a, b) => {
      const getOffsetNumber = (offset: string) => {
        if (offset === 'UTC±0') return 0
        const match = offset.match(/UTC([+-])(\d+)(?::(\d+))?/)
        if (!match) return 0
        const sign = match[1] === '+' ? 1 : -1
        const hours = parseInt(match[2], 10)
        const minutes = match[3] ? parseInt(match[3], 10) : 0
        return sign * (hours + minutes / 60)
      }

      return getOffsetNumber(a.offset) - getOffsetNumber(b.offset)
    })
    .map(tz => ({
      value: tz.value,
      label: tz.label,
    }))
}
